<template>
    <div>
        <Loader :field="'companies'" v-if="loading || listLoading" />
        <div v-else class="job-list">
            <JobListItem
                v-for="(job, index) in displayedItems"
                :key="job._id"
                :job="job"
                :isJobScouted="isJobScouted(job)"
                :company="profile.company_name"
                :index="index"
                :searchHighlights="job.highlights || []"
                @click="getJobsToScout(job, index)"
            />
        </div>
        <div class="pagination-area" v-if="totalPages > 1 && !showMatchedJobs">
            <Pagination
                :total-page="totalPages"
                :callback="handlePageChange"
                :currentPage="currentPage"
            />
        </div>
        <JobDescriptionModal
            ref="jobModal"
            :currentJob="selectedJob"
            :currentIndex="actualJobIndex"
            :totalJobs="totalJobsCount"
            :isJobScouted="isCurrentJobScouted"
            :hasReachedScoutLimit="hasReachedScoutLimit"
            :loading="loadingJobDetails"
            :company="profile.company_name"
            @scout="openScoutConfirmation"
            @next="handleNext"
        />
        <ScoutConfirmationModal
            ref="scoutConfirmModal"
            @confirm="handleScout"
            @cancel="handleScoutCancel"
        />
    </div>
</template>

<script>
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import JobListItem from "./JobListItem.vue";
import Loader from "../Loader.vue";
import Api from "../../config/api";
import Pagination from "../Pagination.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
    name: "JobLists",
    components: {
        JobDescriptionModal,
        ScoutConfirmationModal,
        JobListItem,
        Loader,
        Pagination,
    },
    props: {
        showMatchedJobs: {
            type: Boolean,
            required: true,
        },
        searchKeyword: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            filteredJobs: [],
            jobs: [],
            scoutedJobs: [],
            allJobs: [],
            loading: false,
            selectedJob: {},
            jobsByCompany: [], // Jobs from the same company
            currentJobIndex: 0,
            loadingJobDetails: false,
            showScoutConfirmation: false,
            clientPage: 1,
            pageSize: 20,
        };
    },
    computed: {
        ...mapGetters({
            profile: 'getProfile',
            serverJobs: 'jobs/getJobs',
            totalJobs: 'jobs/getTotalJobs',
            currentPage: 'jobs/getCurrentPage',
            totalPages: 'jobs/getTotalPages',
            listLoading: 'jobs/getLoading'
        }),
        totalJobsCount() {
            return this.showMatchedJobs ? this.filteredJobs.length : this.totalJobs;
        },
        actualJobIndex() {
            if (this.showMatchedJobs) {
                return this.currentJobIndex;
            }
            // Calculate actual index based on current page (20 items per page)
            return ((this.currentPage - 1) * 20) + this.currentJobIndex;
        },
        displayedItems() {
            if (this.showMatchedJobs) {
                const start = (this.clientPage - 1) * this.pageSize;
                const end = start + this.pageSize;

                if(!this.searchKeyword) {
                    this.filteredJobs = this.jobs;
                }
                
                return this.filteredJobs.slice(start, end);
            }
            return this.serverJobs;  // Use jobs from Vuex
        },
        displayedJobs() {
            return this.displayedItems;
            return this.showMatchedJobs ? this.scoutedJobs : this.jobs;
        },
        isCurrentJobScouted() {
            // Check if the currently selected job is scouted
            return this.isJobScouted(this.selectedJob);
        },
        hasReachedScoutLimit() {
            return this.scoutedJobs.length >= 3;
        },
    },
    methods: {
        ...mapActions({
            loadJobs: 'jobs/loadJobs'
        }),
        ...mapMutations({
            setPage: 'setPage'
        }),
        async handleSearch() {
            if (this.showMatchedJobs) {
                // Perform client-side search on existing jobs
                this.performClientSearch();
            } else {
                // Perform server-side search
                await this.performServerSearch();
            }
        },
        performClientSearch() {
            if (!this.searchKeyword.trim()) {
                // If search is empty, show all jobs
                this.filteredJobs = this.jobs;
                return;
            }
            const searchTerms = this.searchKeyword
                .toLowerCase()
                .split(/[,\s]+/)
                .filter((term) => term);

            // Filter jobs that match ALL search terms
            this.filteredJobs = this.jobs.filter((job) => {
                // Create a single searchable text from relevant job fields
                const searchableText = [
                    job.job_title,
                    job.job_description,
                    job.location,
                ]
                    .join(" ")
                    .toLowerCase();

                // Job must match ALL search terms to be included
                return searchTerms.every((term) =>
                    searchableText.includes(term)
                );
            });
        },
        async handlePageChange(page) {
            this.scrollToTop();
            if (this.showMatchedJobs) {
                this.clientPage = page;
            } else {
                await this.loadJobs({ page, searchKeyword: this.searchKeyword });
            }
        },
        async performServerSearch() {
            await this.loadJobs({ page: 1, searchKeyword: this.searchKeyword });
        },
        getJobsToScout(job, index) {
            // Find the index of the clicked job in displayedJobs
            this.currentJobIndex = index;
            if (this.currentJobIndex === -1) this.currentJobIndex = 0;

            // Set the selected job
            this.selectedJob = job;

            // Show the modal
            this.$refs.jobModal.show();
        },
        async handleNext() {
            if (this.currentJobIndex < this.displayedJobs.length - 1) {
                // Move to next job in current page
                this.currentJobIndex = this.currentJobIndex + 1;
                this.selectedJob = this.displayedJobs[this.currentJobIndex];
            } else if (!this.showMatchedJobs && this.currentPage < this.totalPages) {
                // Load next page and show first item
                const nextPage = this.currentPage + 1;
                this.setPage(nextPage);  // Update the page state first
                await this.loadJobs({ page: nextPage, searchKeyword: this.searchKeyword });
                this.currentJobIndex = 0;
                this.selectedJob = this.displayedJobs[this.currentJobIndex];
                this.scrollToTop();
            } else {
                // Close modal if no more jobs
                this.$refs.jobModal.hide();
            }
        },
        isJobScouted(job) {
            return this.scoutedJobs.some(
                (scoutedJob) => scoutedJob._id === job._id
            );
        },
        openScoutConfirmation() {
            this.showScoutConfirmation = true;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal.show();
            });
        },
        handleScoutCancel() {
            this.showScoutConfirmation = false;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal.hide();
            });
        },
        async handleScout() {
            try {
                console.log('submitting scout request');
                await this.submitScoutRequest(this.selectedJob._id);
                this.$nextTick(() => {
                    this.$refs.scoutConfirmModal.hide();
                    // this.handleNext();
                });
            } catch (error) {
                console.error("Scout request failed:", error);
                // Handle error (show error message, etc.)
            }
        },
        async submitScoutRequest(jobId) {
            // Implement your API call here
            try {
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                console.log('here');
                await this.axios({
                    method: "post",
                    url: Api.url(Api.endpoints.addToScout),
                    headers,
                    data: {
                        jobId,
                    },
                });
                const scoutedJob = this.displayedJobs.find(
                    (job) => job._id === jobId
                );
                if (
                    scoutedJob &&
                    !this.scoutedJobs.some((job) => job._id === jobId)
                ) {
                    this.scoutedJobs.push(scoutedJob);
                }
            } catch (err) {
                console.log("Submitting scout request for job:", jobId);
            }
        },
        async getUserBasedJobs() {
            try {
                this.loading = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.allJobs),
                    headers,
                });
                //   console.log(jobs);
                this.filteredJobs = jobs.data;
                this.jobs = jobs.data;
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loading = false;
            }
        },
        async getJobseekerScoutedJobs() {
            try {
                this.loading = true;
                const token = await this.$auth.getToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.scoutedJobs),
                    headers,
                });
                this.scoutedJobs = jobs.data;
                // console.log(this.scoutedJobs);
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loading = false;
            }
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
    async created() {
        await this.getUserBasedJobs();
        await this.getJobseekerScoutedJobs();
        if (!this.showMatchedJobs) {
            await this.loadJobs({ page: 1 });
        }
    },
    watch: {
        showMatchedJobs: {
            handler(newVal) {
                if (!newVal) {
                    this.performServerSearch();
                }
            },
        },
    },
};
</script>

<style scoped>
.jobs {
    justify-content: space-between;
}
.job-list {
    display: flex;
    flex-direction: column;
}

.job-card {
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 1px;
    padding: 16px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.job-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.company-name {
    font-weight: bold;
    font-size: 16px;
    color: #3c3c3c;
    font-weight: 600;
    margin-bottom: 8px;
}

.job-title {
    font-size: 14px;
    font-weight: 600;
    color: #3c3c3c;
}
.scouted-badge {
    margin: auto 0px;
}

.scouted-badge-text {
    width: 60px;
    border: 1px solid #f6ae2d;
    color: #f6ae2d;
    text-align: center;
    font-size: 10px;
}
</style>
